var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useAlert } from 'components/alert/AlertStack';
import { useMedia } from 'components/media/MediaProvider';
import { useModal } from 'components/modal/ModalStack';
import { useUser } from 'components/user/UserProvider';
import { classNames } from 'libs/react';
import { useQuery } from 'libs/react';
import { useState } from 'libs/react';
import { useValue } from 'libs/react';
import { append } from 'libs/util/array';
import { Button } from 'components/Button';
import { FormDelete } from 'components/form/FormDelete';
import { RemovalForm } from 'components/form/graphql/RemovalForm';
import { Icon } from 'components/Icon';
import { MediaGallery } from 'components/media/MediaGallery';
import { MediaInfos } from 'components/media/MediaInfos';
import { MediaUploadButton } from 'components/media/MediaUploadButton';
import { MediaUploadList } from 'components/media/MediaUploadList';
import { Text } from 'components/Text';
import { TableSearch } from 'components/table/private/TableSearch';
import { Waiter } from 'components/Waiter';
import { DeleteMediaMutation } from 'graphql/Media/mutations/DeleteMediaMutation';
import { MediasQuery } from 'graphql/Media/queries/MediasQuery';
import { MediaType } from 'libs/media/MediaType';
import { React } from 'libs/react';
import './MediaLibrary.scss';
/**
 * @component MediaLibrary
 * @since 1.0.0
 */
export function MediaLibrary(props) {
    const { style, types, multiple, medias, fromPublic } = Object.assign({ style: 'normal', types: '', multiple: false }, props);
    const self = useValue(() => ({
        drag: 0
    }));
    const { isContactUser, isManager } = useUser();
    const { uploads, createMedia } = useMedia();
    const [selected, setSelected] = useState(medias || []);
    const [disabled, setDisabled] = useState([]);
    const [dragging, setDragging] = useState(false);
    const [search, setSearch] = useState('');
    const modal = useModal();
    const alert = useAlert();
    const restricted = isContactUser() || isManager();
    const [data, ready, , , refetch] = useQuery(MediasQuery, {
        variables: {
            input: {
                restricted: restricted || undefined,
                query: search
                    ? [{ field: 'filename', operator: 'like', value: [search] }]
                    : null
            }
        }
    });
    /**
     * @function onDragEnter
     * @since 1.0.0
     * @hidden
     */
    function onDragEnter(e) {
        e.preventDefault();
        e.stopPropagation();
        self.drag++;
        if (self.drag == 1) {
            setDragging(true);
        }
    }
    /**
     * @function onDragLeave
     * @since 1.0.0
     * @hidden
     */
    function onDragLeave(e) {
        e.preventDefault();
        e.stopPropagation();
        self.drag--;
        if (self.drag == 0) {
            setDragging(false);
        }
    }
    /**
     * @function onDragOver
     * @since 1.0.0
     * @hidden
     */
    function onDragOver(e) {
        e.preventDefault();
        e.stopPropagation();
    }
    /**
     * @function onDrop
     * @since 1.0.0
     * @hidden
     */
    function onDrop(e) {
        e.preventDefault();
        e.stopPropagation();
        onUpload(Array.from(e.dataTransfer.files));
        setDragging(false);
    }
    /**
     * @function onUpload
     * @since 1.0.0
     * @hidden
     */
    function onUpload(files) {
        return __awaiter(this, void 0, void 0, function* () {
            const accepted = files.filter((file) => MediaType.match(file.type, types));
            const rejected = files.length - accepted.length;
            if (rejected > 0) {
                alert.present({
                    style: 'error',
                    title: (React.createElement("p", null, rejected == 1
                        ? t('1 file was ignored because its type is not supported here.')
                        : t('{{count}} files were ignored because their type is not supported here.').replace('{{count}}', String(rejected))))
                });
            }
            yield Promise.all(accepted.map((file) => __awaiter(this, void 0, void 0, function* () {
                const media = yield createMedia({ file });
                if (media) {
                    onChoose(media);
                }
                return media;
            })));
            yield refetch();
        });
    }
    /**
     * @function onChoose
     * @since 1.0.0
     * @hidden
     */
    function onChoose(media) {
        let selectedCopy = [...selected];
        const match = selectedCopy.find((record) => record.id == media.id);
        if (match) {
            selectedCopy = multiple
                ? selectedCopy.filter((s) => s.id !== media.id)
                : [];
        }
        else {
            selectedCopy = multiple ? append(selectedCopy, media) : [media];
        }
        setSelected(selectedCopy);
    }
    /**
     * @function onDelete
     * @since 1.0.0
     * @hidden
     */
    function onDelete() {
        const onDeleteSuccess = function (media) {
            onChoose(media);
            modal.dismiss();
        };
        const onCancel = function () {
            modal.dismiss();
        };
        modal.present({
            title: t('Delete media'),
            picto: '',
            modal: (React.createElement(DeleteForm, { data: selected[0], onDeleteSuccess: onDeleteSuccess, onCancel: onCancel })),
            className: 'media-picker-delete-form'
        });
    }
    /**
     * @function onBulkDelete
     * @since 1.0.0
     * @hidden
     */
    function onBulkDelete() {
        const onDeleteSuccess = function () {
            return __awaiter(this, void 0, void 0, function* () {
                setSelected([]);
                yield refetch();
                modal.dismiss();
            });
        };
        const onCancel = function () {
            modal.dismiss();
        };
        modal.present({
            title: t('Delete medias'),
            picto: '',
            modal: (React.createElement(BulkDeleteForm, { medias: selected, onDeleteSuccess: onDeleteSuccess, onCancel: onCancel })),
            className: 'media-picker-delete-form'
        });
    }
    /**
     * @function onSelect
     * @since 1.0.0
     * @hidden
     */
    function onSelect() {
        if (props.onSelect) {
            props.onSelect(selected);
        }
    }
    /**
     * @function onSearch
     * @since 1.0.0
     * @hidden
     */
    function onSearch(value) {
        setSearch(value || '');
    }
    const current = selected && selected[0];
    const className = classNames('media-library', 'media-library--' + style, { 'media-library--is-selected': selected }, { 'media-library--is-multiple': multiple && selected.length > 1 }, { 'media-library--is-dragging': dragging }, props.className);
    return (React.createElement("div", { className: className, onDragEnter: onDragEnter, onDragLeave: onDragLeave, onDragOver: onDragOver, onDrop: onDrop },
        React.createElement("div", { className: "media-library-body" },
            React.createElement("div", { className: "media-library-side" }, current && (React.createElement(React.Fragment, null,
                React.createElement(MediaInfos, { media: current, onDelete: onDelete })))),
            React.createElement("div", { className: "media-library-main" },
                React.createElement("div", { className: "media-library-toolbar" },
                    React.createElement(TableSearch, { placeholder: t('Search by file name'), onSearch: onSearch }),
                    multiple && selected.length > 0 && (React.createElement(Button, { className: "media-library-bulk-delete", style: "warning", picto: "trash", label: t('Delete selected ({{count}})').replace('{{count}}', String(selected.length)), onPress: onBulkDelete }))),
                React.createElement("div", { className: "media-library-gallery" },
                    React.createElement(Waiter, { until: ready, renderWhileLoading: true }, !(data === null || data === void 0 ? void 0 : data.medias) || data.medias.length === 0 ? (React.createElement("div", { className: "media-library-empty" },
                        React.createElement(Icon, { className: "media-library-empty-picto", value: "images fal" }),
                        React.createElement(Text, { className: "media-library-empty-title", value: search
                                ? t('No media matches your search.')
                                : t('No media yet. Upload a file to get started.') }))) : (React.createElement(MediaGallery, { types: types, medias: data.medias, selected: selected, disabled: disabled, onPress: onChoose })))),
                React.createElement(MediaUploadList, { uploads: uploads }),
                React.createElement("div", { className: "media-library-drop" },
                    React.createElement(Icon, { className: "media-library-drop-picto", value: "download fal" }),
                    React.createElement(Text, { className: "media-library-drop-title", value: t('Drop files here') })))),
        React.createElement("div", { className: "media-library-foot" },
            React.createElement("div", { className: "media-library-side-buttons" },
                React.createElement(MediaUploadButton, { onUpload: onUpload, types: types })),
            React.createElement("div", { className: "media-library-main-buttons" },
                React.createElement(Button, { style: "primary", label: t('Select'), picto: "check", disabled: selected.length == 0, onPress: onSelect })))));
}
/**
 * @interface DeleteForm
 * @since 1.0.0
 */
function DeleteForm(props) {
    const { data } = props;
    const alert = useAlert();
    /**
     * @function onSuccess
     * @since 1.0.0
     * @hidden
     */
    function onProcess() {
        const input = {
            id: data.id
        };
        return {
            variables: {
                input
            },
            refetchQueries: ['MediasQuery'],
            awaitRefetchQueries: true,
            update(cache) {
                //TODO : This does not work
                const normalizedId = cache.identify({
                    id: data.id,
                    __typename: 'Media'
                });
                if (normalizedId) {
                    cache.evict({ id: normalizedId });
                    cache.gc();
                }
            }
        };
    }
    /**
     * @function onSuccess
     * @since 1.0.0
     * @hidden
     */
    function onSuccess() {
        if (props.onDeleteSuccess) {
            props.onDeleteSuccess(data);
        }
    }
    /**
     * @function onFailure
     * @since 1.0.0
     * @hidden
     */
    function onFailure(error) {
        alert.present({ error });
    }
    return (React.createElement(RemovalForm, { label: t('Delete this media'), infos: t('This action cannot be undone.'), message: React.createElement("p", null,
            t('Remove'),
            " ",
            React.createElement("strong", null,
                "\u00AB ",
                data.filename,
                " \u00BB"),
            ' ',
            t('this media from the list of medias.')), mutation: DeleteMediaMutation, onProcess: onProcess, onSuccess: onSuccess, onFailure: onFailure, onCancel: props.onCancel }));
}
/**
 * @interface BulkDeleteForm
 * @since 1.0.0
 */
function BulkDeleteForm(props) {
    const { medias } = props;
    const { deleteMedia } = useMedia();
    const alert = useAlert();
    const [processing, setProcessing] = useState(false);
    /**
     * @function onAccept
     * @since 1.0.0
     * @hidden
     */
    function onAccept() {
        return __awaiter(this, void 0, void 0, function* () {
            setProcessing(true);
            try {
                yield Promise.all(medias.map((media) => deleteMedia({ id: media.id })));
                if (props.onDeleteSuccess) {
                    props.onDeleteSuccess();
                }
            }
            catch (error) {
                alert.present({ error: error });
            }
            finally {
                setProcessing(false);
            }
        });
    }
    return (React.createElement("div", { className: "delete-form" },
        React.createElement(FormDelete, { label: t('Delete these medias'), infos: t('This action cannot be undone.'), message: React.createElement("p", null,
                t('Remove'),
                ' ',
                React.createElement("strong", null,
                    medias.length,
                    ' ',
                    medias.length == 1 ? t('media') : t('medias')),
                ' ',
                t('from the list of medias.')), onAccept: processing ? undefined : onAccept, onCancel: props.onCancel })));
}
